import ls from "Localization";
import ReportTable from "components/Report";
import dayjs from "dayjs";
import customDateFormat from "dayjs/plugin/customParseFormat";
import { cleanCpfCnpj, stringToMask } from "helpers/string";
import React, { createRef, useRef, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Col, Container, Row } from "reactstrap";
import * as api from "services/api";
dayjs.extend(customDateFormat);

export default function DasnReport() {
	const { token } = useSelector(s => s.login);
	const [pages, setPages] = useState(0);
	const [count, setCount] = useState(0);
	const [statusCount, setStatusCount] = useState({
		PENDING: 0,
		STARTED: 0,
		UPDATING: 0,
		UPDATED: 0,
		ERROR: 0
	});
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const table = useRef(null);
	const UpdatingDasnStatus = {
		PENDING: "Pendente",
		STARTED: "Iniciado",
		UPDATING: "Atualizando",
		UPDATED: "Atualizado",
		ERROR: "Erro"
	};

	const UpdatingDasnStatusColor = {
		PENDING: "badge-secondary",
		STARTED: "badge-info",
		UPDATING: "badge-warning",
		UPDATED: "badge-success",
		ERROR: "badge-danger"
	};

	const handleFetchData = async tableState => {
		try {
			if (!tableState) return;
			createRef(tableState);

			let { page, pageSize, sorted, filtered } = tableState;

			setLoading(true);

			const response = await api.sendGet(
				`/dasn/last-updated?skip=${page * pageSize}&take=${pageSize}&sort=${
					sorted[0] ? sorted[0].id : undefined
				}&desc=${sorted[0] ? sorted[0].desc : undefined}` +
					filtered.reduce((p, c) => {
						return p + `&filters[${c.id}]=${c.value}`;
					}, ""),
				{
					Authorization: "Bearer " + token
				}
			);

			const result = await response?.json();

			if (result.isValid) {
				setData(result?.model);
				setCount(result?.params.count);
				setPages(Math.ceil(result?.params.count / pageSize));
				setStatusCount({
					PENDING: result?.params?.PENDING || 0,
					STARTED: result?.params?.STARTED || 0,
					UPDATING: result?.params?.UPDATING || 0,
					UPDATED: result?.params?.UPDATED || 0,
					ERROR: result?.params?.ERROR || 0
				});
			}

			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Error fetching data:", error);
			toast.error("Erro ao buscar dados");
		}
	};

	return (
		<Container className="dashboard">
			<Row className="d-flex justify-content-between align-items-center mb-4">
				{Object.keys(UpdatingDasnStatus).map(statusKey => {
					const countByStatus = statusCount[statusKey] || 0;
					return (
						<Col key={statusKey} md={2} sm={4} xs={6}>
							<div
								className="shadow-sm rounded d-flex flex-column align-items-center justify-content-center py-3"
								style={{
									background: "#fff",
									borderLeft: `6px solid var(--${UpdatingDasnStatusColor[
										statusKey
									].replace("badge-", "")}, #007bff)`,
									minHeight: 110
								}}
							>
								<span
									className={`badge ${UpdatingDasnStatusColor[statusKey]}`}
									style={{ fontSize: 14, marginBottom: 8 }}
								>
									{UpdatingDasnStatus[statusKey]}
								</span>
								<h3 style={{ fontWeight: 700, color: "#222" }}>
									{countByStatus}
								</h3>
							</div>
						</Col>
					);
				})}
			</Row>

			<Row>
				<Col md={12}>
					<ReportTable
						manual
						tableRef={table}
						title={"Lista DASN Atualizadas"}
						data={data}
						loading={loading}
						noExport={true}
						onFetchData={handleFetchData}
						pages={pages}
						count={count}
						defaultPageSize={15}
						filterable
						defaultSorted={[
							{
								id: "provider.lastUpdatedDasn",
								desc: false
							}
						]}
						columns={[
							{
								Header: (
									<span>
										Data da <br />
										última atualização
									</span>
								),
								id: "provider.lastUpdateDasn",
								accessor: ({ provider }) =>
									provider.lastUpdatedDasn
										? dayjs(provider.lastUpdatedDasn).format(
												ls.dateFormatWithoutSeconds
										  )
										: "",
								width: 150
							},
							{
								Header: "CNPJ",
								id: "provider.cnpj",
								Cell: ({ value }) => (
									<div style={{ display: "flex", alignItems: "center" }}>
										<button
											onClick={() => {
												navigator.clipboard
													.writeText(cleanCpfCnpj(value))
													.then(() => {
														toast.success(
															"CNPJ copiado para a área de transferência!"
														);
													});
											}}
											style={{
												marginLeft: "8px",
												background: "none",
												border: "none",
												cursor: "pointer"
											}}
											title="Copiar para a área de transferência"
										>
											<FaRegCopy color="blue" />
											<span> {value}</span>
										</button>
									</div>
								),
								accessor: ({ provider }) =>
									provider.cnpj
										? stringToMask(provider.cnpj, "cnpj")
										: "Não definido",
								width: 200
							},
							{
								Header: "Empresa",
								id: "provider.businessName",
								accessor: ({ provider }) => {
									return (
										<button
											onClick={() =>
												window.open(
													`/companies/${provider._id}`,
													"_blank",
													"noopener,noreferrer"
												)
											}
											style={{
												background: "none",
												border: "none",
												cursor: "pointer",
												display: "flex",
												alignItems: "center"
											}}
											title={"Abrir empresa do prestador"}
										>
											<span style={{ marginRight: 6 }}>
												<FiExternalLink color="blue" title="Abrir empresa" />
											</span>
											{provider.businessName || "Não definido"}
										</button>
									);
								}
							},
							{
								Header: "Contratante",
								id: "business.businessName",
								accessor: c =>
									c.business && c.business.businessName
										? c.business.businessName
										: "Não definido"
							},
							{
								Header: (
									<span>
										Status
										<br />
										Prestador
									</span>
								),
								id: "status",
								filterable: false,
								accessor: ({ status }) => {
									switch (status) {
										case 1:
											return (
												<span className="w-100 badge badge-warning">
													Onboard
												</span>
											);
										case 2:
											return (
												<span className="w-100 badge badge-success">Ativo</span>
											);
										case 3:
											return (
												<span className="w-100 badge badge-danger">
													Inativo
												</span>
											);
										default:
										case 0:
											return (
												<span className="w-100 badge badge-secondary">
													Pending
												</span>
											);
									}
								},
								Filter: ({ filter, onChange }) => {
									return (
										<select
											onChange={event => onChange(event.target.value)}
											style={{ width: "100%" }}
											value={filter && filter.value ? filter.value : "all"}
										>
											<option value="">Todos</option>
											{Object.keys(UpdatingDasnStatus)
												.filter(c => !isNaN(+c))
												.map(c => (
													<option key={c} value={c}>
														{UpdatingDasnStatus[c]}
													</option>
												))}
										</select>
									);
								},
								width: 90
							},
							{
								Header: "Status",
								id: "provider.updatingDasnStatus",
								accessor: ({ provider }) => {
									return (
										<span
											className={`w-100 badge ${
												UpdatingDasnStatusColor[provider?.updatingDasnStatus]
											}`}
										>
											{UpdatingDasnStatus[provider?.updatingDasnStatus]}
										</span>
									);
								},
								Filter: ({ filter, onChange }) => {
									return (
										<select
											onChange={event => onChange(event.target.value)}
											style={{ width: "100%" }}
											value={filter && filter.value ? filter.value : "all"}
										>
											<option value="">Todos</option>
											{Object.keys(UpdatingDasnStatus).map(c => (
												<option key={c} value={c}>
													{UpdatingDasnStatus[c]}
												</option>
											))}
										</select>
									);
								},
								width: 90
							},
							{
								Header: "Observação",
								id: "provider.updatingDasnStatusErrorMessage",
								accessor: ({ provider }) =>
									provider?.updatingDasnStatusErrorMessage || "",
								Cell: ({ value }) => {
									const maxLength = 30;
									const displayText =
										value && value.length > maxLength
											? value.slice(0, maxLength) + "..."
											: value;
									return (
										<span
											title={value}
											style={{ cursor: value ? "pointer" : "default" }}
										>
											{displayText}
										</span>
									);
								},
								width: 200
							}
						]}
					/>
				</Col>
			</Row>
		</Container>
	);
}
